/**
 * The ONE reader of «is the guide leading right now».
 *
 * The answer is a PROP of the map card (`mode: "card" | "nav"`), not a flag
 * in a store and not an element in `region:header`, and the reasons are worth
 * keeping (design §2.1):
 *
 *  * it is a fact about the MAP, so it is read where the map is drawn — one
 *    authority, no rule about what happens when two of them disagree;
 *  * it survives an F5 for free. `values.board.surface` comes back from
 *    `/state`, `restore.ts` wraps it in a `createSurface` with no translation,
 *    and the card arrives already carrying `mode: "nav"`;
 *  * an ABSENT prop reads as `"card"`, so every board frame written before the
 *    navigation mode existed keeps working exactly as it did.
 *
 * The frontend never turns the mode off by itself: leaving is a board frame in
 * which the card came back as `preview` or left the board. The one exception
 * lives in `navStore.forcedExit`, for a run that failed — nobody should be
 * stuck full-screen because of a 500.
 */

import { useEffect, useMemo } from 'react';

import type { MapCardProps } from '../catalog/generated/schemas';
import { REGION_IDS, useSurface, type Region, type SurfaceNode } from '../a2ui/surfaceStore';
import { useNav } from './navStore';

const REGION_ORDER: Region[] = ['header', 'main', 'side', 'footer'];

export type NavCard = { nodeId: string; props: MapCardProps };

function findNavCard(): NavCard | null {
  const state = useSurface.getState();
  const found: NavCard[] = [];
  for (const region of REGION_ORDER) {
    // `childrenOf`, NOT `regionNodes`: the latter is the RENDER order and
    // includes ids that have already left the region and are still animating
    // out (`exiting`). A card the board has taken away is not the guide
    // leading — reading it would hold the whole screen in navigation mode for
    // the length of an exit animation after `nav_stop`, which is the one
    // moment the visitor is watching for the board to come back.
    for (const id of state.childrenOf(REGION_IDS[region])) {
      const node = state.components[id];
      if (!node || node.component !== 'MapCard') continue;
      const props = node.props as MapCardProps;
      if (props?.mode !== 'nav') continue;
      found.push({ nodeId: node.id, props });
    }
  }
  if (found.length === 0) return null;
  if (found.length > 1) {
    // eslint-disable-next-line no-console
    console.warn('[nav] more than one map card is in nav mode; taking the first');
  }
  return found[0]!;
}

/** The card the guide is leading with, or `null`. Re-derived per board frame. */
export function useNavCard(): NavCard | null {
  const components = useSurface((state) => state.components);
  const exiting = useSurface((state) => state.exiting);
  const forcedExit = useNav((state) => state.forcedExit);
  const card = useMemo(() => {
    void components;
    void exiting;
    return findNavCard();
    // The two maps ARE the tree; zustand compares by reference, so a selector
    // returning a fresh object would re-render forever (`Board.tsx`'s rule).
  }, [components, exiting]);

  /**
   * The board stopped saying «nav», so the SESSION is over.
   *
   * Two things end together and it is the same moment for both. `forcedExit` is
   * the escape hatch for a failed run — the visitor pressed «Стоп» twice, and
   * nobody may be trapped full-screen by a 500 — and it is a claim about a
   * board that is out of date, so it stops being true the frame the board
   * agrees; left latched it would be permanent and the guide could never lead
   * this visitor again in this thread. Everything else the session held goes
   * with it (`navStore.endSession`): `nav_stop` swaps `map:nav` for a `map:plan`
   * in `card` mode, and a `stopping` flag surviving that would make the FIRST
   * «Стоп» of the next session read as its second press.
   */
  useEffect(() => {
    if (!card) useNav.getState().endSession();
  }, [card, forcedExit]);

  return forcedExit ? null : card;
}

export function useNavMode(): { active: boolean; nodeId: string | null } {
  const card = useNavCard();
  return { active: card !== null, nodeId: card?.nodeId ?? null };
}

/** The same question outside React — for a store or an event handler. */
export function navCardNow(): NavCard | null {
  if (useNav.getState().forcedExit) return null;
  return findNavCard();
}

export type { SurfaceNode };
